import { AlertTriangle } from "lucide-react";

import type {
  CaptureResponse,
  InquiryCreatedResponse,
  InquiryFinalizedResponse,
} from "@/api";
import { BodyViewer, PayloadViewer } from "@/components/PayloadViewer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Vertical timeline of events for one inquiry. Three section types:
 *
 * 1. ``inquiry_created`` card — config snapshot of which handlers were
 *    expected to fire, plus chat/bot context. Annotated with a warning
 *    when ``dealership_id`` is null (the spec calls this out).
 * 2. ``capture`` cards — one per egress event, ordered by ``captured_at``.
 *    Outcome badge + request/response payload viewers + correlation ID.
 * 3. ``inquiry_finalized`` card — handlers attempted/skipped + the
 *    ``external_data`` snapshot.
 *
 * Each card carries an ``id`` attribute so the detection-panel side
 * can scroll-and-pulse the relevant card via ``triggerPulse``. ``pulsedId``
 * drives a one-shot ring outline (cleared by the parent after ~2s).
 */

export const ANCHOR_INQUIRY_CREATED = "card-inquiry-created";
export const ANCHOR_INQUIRY_FINALIZED = "card-inquiry-finalized";

export const captureAnchor = (captureId: number): string =>
  `card-capture-${captureId}`;

export function EventTimeline({
  inquiryCreated,
  captures,
  inquiryFinalized,
  pulsedId,
}: {
  inquiryCreated: InquiryCreatedResponse | null;
  captures: CaptureResponse[];
  inquiryFinalized: InquiryFinalizedResponse | null;
  pulsedId: string | null;
}): JSX.Element {
  return (
    <ol className="space-y-4">
      {inquiryCreated !== null && (
        <li>
          <InquiryCreatedCard
            data={inquiryCreated}
            pulsed={pulsedId === ANCHOR_INQUIRY_CREATED}
          />
        </li>
      )}
      {captures.map((capture) => (
        <li key={capture.id}>
          <CaptureCard
            capture={capture}
            pulsed={pulsedId === captureAnchor(capture.id)}
          />
        </li>
      ))}
      {inquiryFinalized !== null && (
        <li>
          <InquiryFinalizedCard
            data={inquiryFinalized}
            pulsed={pulsedId === ANCHOR_INQUIRY_FINALIZED}
          />
        </li>
      )}
    </ol>
  );
}

const PULSE_RING = "ring-4 ring-primary/40";

function InquiryCreatedCard({
  data,
  pulsed,
}: {
  data: InquiryCreatedResponse;
  pulsed: boolean;
}): JSX.Element {
  const dealershipMissing = data.dealership_id === null;
  return (
    <Card
      id={ANCHOR_INQUIRY_CREATED}
      className={cn("transition-shadow duration-500", pulsed && PULSE_RING)}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Inquiry created
          {dealershipMissing && (
            <span
              className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800"
              title="dealership_id is null — the dispatcher's guard would skip all handlers"
            >
              <AlertTriangle className="h-3 w-3" />
              missing dealership_id
            </span>
          )}
        </CardTitle>
        <CardDescription>
          <span title={data.captured_at}>
            {new Date(data.captured_at).toISOString()}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <DefinitionList
          items={[
            ["chat_reference", data.chat_reference ?? "—"],
            // bot_id and dealership_id are integers (they mirror
            // public.inquiry.botid / .dealershipid); DefinitionList renders
            // strings, so stringify rather than lean on JSX coercion.
            ["bot_id", data.bot_id === null ? "—" : String(data.bot_id)],
            [
              "dealership_id",
              data.dealership_id === null ? "—" : String(data.dealership_id),
            ],
          ]}
        />
        <Section title="config_snapshot">
          <PayloadViewer value={data.config_snapshot} />
        </Section>
      </CardContent>
    </Card>
  );
}

const OUTCOME_VARIANT: Record<string, string> = {
  success: "bg-emerald-100 text-emerald-800 border-emerald-200",
  http_error: "bg-orange-100 text-orange-800 border-orange-200",
  handler_error: "bg-orange-100 text-orange-800 border-orange-200",
  no_response: "bg-red-100 text-red-800 border-red-200",
  pre_egress_error: "bg-red-100 text-red-800 border-red-200",
};

function CaptureCard({
  capture,
  pulsed,
}: {
  capture: CaptureResponse;
  pulsed: boolean;
}): JSX.Element {
  const request = capture.request;
  const response = capture.response;

  const requestMethod = typeof request.method === "string" ? request.method : "?";
  const requestUrl = typeof request.url === "string" ? request.url : "";
  const responseStatus =
    response && typeof response.status === "number" ? response.status : null;
  const durationMs =
    response && typeof response.duration_ms === "number"
      ? response.duration_ms
      : null;

  const outcomeClass =
    OUTCOME_VARIANT[capture.outcome] ??
    "bg-slate-100 text-slate-800 border-slate-200";

  return (
    <Card
      id={captureAnchor(capture.id)}
      className={cn("transition-shadow duration-500", pulsed && PULSE_RING)}
    >
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{capture.integration_slug}</span>
          <span
            className={cn(
              "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold",
              outcomeClass,
            )}
          >
            {capture.outcome}
          </span>
        </CardTitle>
        <CardDescription>
          <span title={capture.captured_at}>
            {new Date(capture.captured_at).toISOString()}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <DefinitionList
          items={[
            ["method", requestMethod],
            ["url", requestUrl || "—"],
            [
              "status",
              responseStatus !== null ? String(responseStatus) : "—",
            ],
            [
              "duration",
              durationMs !== null ? `${durationMs}ms` : "—",
            ],
          ]}
        />
        <Collapsible summary="Request body">
          <BodyViewer
            body={typeof request.body === "string" ? request.body : null}
          />
        </Collapsible>
        <Collapsible summary="Request headers">
          <PayloadViewer value={request.headers ?? {}} />
        </Collapsible>
        <Collapsible summary="Response body">
          <BodyViewer
            body={
              response && typeof response.body === "string"
                ? response.body
                : null
            }
          />
        </Collapsible>
        <Collapsible summary="Response headers">
          <PayloadViewer value={response?.headers ?? {}} />
        </Collapsible>
        {capture.error !== null && (
          <Section title="error">
            <PayloadViewer value={capture.error} />
          </Section>
        )}
        <Collapsible summary="Egress correlation ID">
          <code className="font-mono text-xs">
            {capture.egress_correlation_id}
          </code>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

function InquiryFinalizedCard({
  data,
  pulsed,
}: {
  data: InquiryFinalizedResponse;
  pulsed: boolean;
}): JSX.Element {
  return (
    <Card
      id={ANCHOR_INQUIRY_FINALIZED}
      className={cn("transition-shadow duration-500", pulsed && PULSE_RING)}
    >
      <CardHeader>
        <CardTitle>Inquiry finalized</CardTitle>
        <CardDescription>
          <span title={data.captured_at}>
            {new Date(data.captured_at).toISOString()}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <DefinitionList
          items={[
            [
              "post_creation_processed",
              data.post_creation_processed ? "true" : "false",
            ],
            [
              "handlers_attempted",
              data.handlers_attempted.length === 0
                ? "(none)"
                : data.handlers_attempted.join(", "),
            ],
          ]}
        />
        {data.handlers_skipped.length > 0 && (
          <Section title="handlers_skipped">
            <PayloadViewer value={data.handlers_skipped} />
          </Section>
        )}
        <Collapsible summary="external_data">
          <PayloadViewer value={data.external_data} />
        </Collapsible>
      </CardContent>
    </Card>
  );
}

function DefinitionList({
  items,
}: {
  items: Array<[string, string]>;
}): JSX.Element {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="font-mono text-xs break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function Collapsible({
  summary,
  children,
}: {
  summary: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <details className="group rounded-md border border-input bg-muted/30 px-3 py-2">
      <summary className="cursor-pointer text-xs font-medium text-muted-foreground group-open:mb-2">
        {summary}
      </summary>
      {children}
    </details>
  );
}
